import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { X, UploadCloud, User, Lock, Camera, Save, Eye, EyeOff, Check, AlertCircle, Sparkles, CreditCard, Shield, Trash2, Download } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';

interface UserProfileData {
  first_name?: string;
  last_name?: string;
  bio?: string;
}

interface ChangePasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const UserProfileForm: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'privacy' | 'billing'>('profile');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatar_url || null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0]) {
      setAvatarFile(acceptedFiles[0]);
      setAvatarPreview(URL.createObjectURL(acceptedFiles[0]));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.png'] },
    multiple: false,
  });

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<UserProfileData>({
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      bio: user?.bio || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm<ChangePasswordData>();

  const newPassword = watch('new_password');

  const onProfileSubmit = async (data: UserProfileData) => {
    setIsLoading(true);
    setError(null);
    setMessage(null);

    try {
      let avatarUrl = user?.avatar_url;

      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        const response = await api.post('/users/profile/avatar', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        avatarUrl = response.data.avatar_url;
      }

      await api.put('/users/profile', { ...data, avatar_url: avatarUrl });
      await refreshUser();
      setMessage('Profile updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data: ChangePasswordData) => {
    setIsPasswordLoading(true);
    setError(null);
    setMessage(null);

    try {
      await api.post('/api/v1/users/change-password', {
        current_password: data.current_password,
        new_password: data.new_password,
      });
      setMessage('Password changed successfully');
      resetPasswordForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleExportData = async () => {
    setIsLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await api.post('/gdpr/export-my-data');
      setMessage('Data export initiated. You will receive a download link shortly.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to initiate data export');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsDeleting(true);
    setError(null);
    setMessage(null);

    try {
      await api.delete(`/gdpr/delete-my-account?confirmation=${deleteConfirmation}&soft_delete=false`);
      setMessage('Account deletion initiated. You will be logged out shortly.');
      // Redirect after a delay
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                Account Settings
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
                Manage your profile and security settings
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="p-3 rounded-xl bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-all duration-200 shadow-2xl hover:shadow-indigo-200/50 dark:hover:shadow-indigo-500/10"
            >
              <X size={20} />
            </motion.button>
          </div>
        </motion.div>

        {/* Main Container */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden"
        >
          
          {/* Tab Navigation */}
          <div className="border-b border-slate-200/50 dark:border-slate-700/50">
            <nav className="flex px-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab('profile')}
                className={`relative py-4 px-4 font-medium text-sm transition-all duration-300 ${
                  activeTab === 'profile'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>Profile</span>
                </div>
                {activeTab === 'profile' && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-blue-500"
                  />
                )}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab('password')}
                className={`relative py-4 px-4 font-medium text-sm transition-all duration-300 ${
                  activeTab === 'password'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Lock size={16} />
                  <span>Security</span>
                </div>
                {activeTab === 'password' && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-blue-500"
                  />
                )}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab('privacy')}
                className={`relative py-4 px-4 font-medium text-sm transition-all duration-300 ${
                  activeTab === 'privacy'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Shield size={16} />
                  <span>Privacy & Data</span>
                </div>
                {activeTab === 'privacy' && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-blue-500"
                  />
                )}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab('billing')}
                className={`relative py-4 px-4 font-medium text-sm transition-all duration-300 ${
                  activeTab === 'billing'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CreditCard size={16} />
                  <span>Billing</span>
                </div>
                {activeTab === 'billing' && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-blue-500"
                  />
                )}
              </motion.button>
            </nav>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Success/Error Messages */}
            <AnimatePresence>
              {message && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <Check size={16} className="text-white" />
                    </div>
                    <p className="text-green-600 dark:text-green-400 font-medium">{message}</p>
                  </div>
                </motion.div>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                      <AlertCircle size={16} className="text-white" />
                    </div>
                    <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {activeTab === 'profile' && (
                <motion.form
                  key="profile"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleProfileSubmit(onProfileSubmit)}
                  className="space-y-6"
                >
                  {/* Profile Picture Section */}
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-2">Profile Picture</h3>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        {...getRootProps()}
                        className="relative group cursor-pointer transition-all duration-300"
                      >
                        {avatarPreview ? (
                          <div className="relative">
                            <img 
                              src={avatarPreview} 
                              alt="Avatar Preview" 
                              className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 dark:border-slate-600" 
                            />
                            <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                              <Camera size={16} className="text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className={`w-20 h-20 rounded-full flex flex-col items-center justify-center border-2 transition-colors duration-200 ${
                            isDragActive 
                              ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-400 dark:border-indigo-500' 
                              : 'bg-slate-100 dark:bg-slate-700/50 border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500'
                          }`}>
                            <User size={20} className="text-slate-400 dark:text-slate-400 mb-1" />
                            <UploadCloud size={12} className="text-slate-400 dark:text-slate-400" />
                          </div>
                        )}
                        <input {...getInputProps()} />
                      </motion.div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {avatarPreview ? 'Change your photo' : 'Upload a photo'}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        Click the circle or drag and drop. PNG, JPG up to 10MB
                      </p>
                    </div>
                  </div>

                  {/* Credits Section */}
                  <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-indigo-600 dark:bg-indigo-500 rounded-lg flex items-center justify-center">
                          <CreditCard size={16} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">Your Credits</h3>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400">
                            For AI-powered tasks
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                          {user?.credits !== undefined ? user.credits.toFixed(2) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Personal Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          First Name
                        </label>
                        <input
                          {...registerProfile('first_name')}
                          type="text"
                          className="w-full px-4 py-3 bg-white dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-black dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 backdrop-blur-sm"
                          placeholder="Enter your first name"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Last Name
                        </label>
                        <input
                          {...registerProfile('last_name')}
                          type="text"
                          className="w-full px-4 py-3 bg-white dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-black dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 backdrop-blur-sm"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                        Bio
                      </label>
                      <textarea
                        {...registerProfile('bio')}
                        rows={3}
                        className="w-full px-4 py-3 bg-white dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-black dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 backdrop-blur-sm resize-none"
                        placeholder="Tell us about yourself..."
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isLoading}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          <span>Save Changes</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.form>
              )}

              {activeTab === 'password' && (
                <motion.form
                  key="password"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                  className="space-y-6"
                >
                  {/* Security Header */}
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Change Password</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Keep your account secure with a strong password
                    </p>
                  </div>

                  {/* Password Fields */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                        Current Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-slate-400 dark:text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        </div>
                        <input
                          {...registerPassword('current_password', {
                            required: 'Current password is required',
                          })}
                          type={showCurrentPassword ? "text" : "password"}
                          className="w-full pl-12 pr-12 py-3 bg-white dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-black dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 backdrop-blur-sm"
                          placeholder="Enter your current password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
                        >
                          {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {passwordErrors.current_password && (
                        <p className="text-xs text-red-500 dark:text-red-400 flex items-center space-x-1 mt-1">
                          <span>⚠️</span>
                          <span>{passwordErrors.current_password.message}</span>
                        </p>
                      )}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                        New Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-slate-400 dark:text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        </div>
                        <input
                          {...registerPassword('new_password', {
                            required: 'New password is required',
                            minLength: {
                              value: 8,
                              message: 'Password must be at least 8 characters',
                            },
                          })}
                          type={showNewPassword ? "text" : "password"}
                          className="w-full pl-12 pr-12 py-3 bg-white dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-black dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 backdrop-blur-sm"
                          placeholder="Enter your new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
                        >
                          {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {passwordErrors.new_password && (
                        <p className="text-xs text-red-500 dark:text-red-400 flex items-center space-x-1 mt-1">
                          <span>⚠️</span>
                          <span>{passwordErrors.new_password.message}</span>
                        </p>
                      )}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                        Confirm New Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-slate-400 dark:text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        </div>
                        <input
                          {...registerPassword('confirm_password', {
                            required: 'Please confirm your new password',
                            validate: value =>
                              value === newPassword || 'Passwords do not match',
                          })}
                          type={showConfirmPassword ? "text" : "password"}
                          className="w-full pl-12 pr-12 py-3 bg-white dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-black dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 backdrop-blur-sm"
                          placeholder="Confirm your new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {passwordErrors.confirm_password && (
                        <p className="text-xs text-red-500 dark:text-red-400 flex items-center space-x-1 mt-1">
                          <span>⚠️</span>
                          <span>{passwordErrors.confirm_password.message}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Security Tips */}
                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                    <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">Password Security Tips</h4>
                    <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                      <li>• Use at least 8 characters</li>
                      <li>• Include uppercase and lowercase letters</li>
                      <li>• Add numbers and special characters</li>
                    </ul>
                  </div>

                  {/* Change Password Button */}
                  <div className="flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isPasswordLoading}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                    >
                      {isPasswordLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Changing...</span>
                        </>
                      ) : (
                        <>
                          <Lock size={16} />
                          <span>Change Password</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.form>
              )}

              {activeTab === 'privacy' && (
                <motion.div
                  key="privacy"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Privacy Header */}
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Privacy & Data Management</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Manage your personal data and privacy settings
                    </p>
                  </div>

                  {/* Data Export Section */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center mt-1">
                          <Download size={16} className="text-white" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200">Export Your Data</h4>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Download a copy of all your personal data including posts, media files, and account information.
                          </p>
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleExportData}
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Exporting...</span>
                          </>
                        ) : (
                          <>
                            <Download size={14} />
                            <span>Export Data</span>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>

                  {/* GDPR Rights Section */}
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-600 dark:bg-green-500 rounded-lg flex items-center justify-center mt-1">
                        <Shield size={16} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">Your Privacy Rights</h4>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1 mb-3">
                          Under GDPR and other privacy laws, you have rights regarding your personal data.
                        </p>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => navigate('/app/gdpr-compliance')}
                          className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center space-x-2"
                        >
                          <Shield size={14} />
                          <span>Manage Privacy Rights</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  {/* Account Deletion Section */}
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-red-600 dark:bg-red-500 rounded-lg flex items-center justify-center mt-1">
                          <Trash2 size={16} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-red-800 dark:text-red-200">Delete Account</h4>
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Permanently delete your account and all associated data. This action cannot be undone.
                          </p>
                        </div>
                      </div>

                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <h5 className="text-xs font-semibold text-red-800 dark:text-red-200 mb-2">
                          What will be deleted:
                        </h5>
                        <ul className="text-xs text-red-700 dark:text-red-300 space-y-1">
                          <li>• Your profile and account information</li>
                          <li>• All posts, drafts, and content</li>
                          <li>• Uploaded media files and assets</li>
                          <li>• Client profiles and team memberships</li>
                          <li>• All analytics and usage data</li>
                        </ul>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-red-700 dark:text-red-300 mb-2">
                            Type "DELETE" to confirm account deletion:
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            placeholder="DELETE"
                            className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                            style={{ 
                              color: '#000000',
                              backgroundColor: '#ffffff',
                              border: '1px solid #fca5a5',
                              fontSize: '14px',
                              fontWeight: 'normal'
                            }}
                          />
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleDeleteAccount}
                          disabled={isDeleting || deleteConfirmation !== 'DELETE'}
                          className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                          {isDeleting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                              <span>Deleting Account...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 size={16} />
                              <span>Delete My Account Permanently</span>
                            </>
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {activeTab === 'billing' && (
                <motion.div
                  key="billing"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Billing & Subscriptions</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Manage your subscription, billing, and payment methods
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/app/billing')}
                      className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
                          <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-left">
                          <h4 className="font-medium text-slate-900 dark:text-slate-100">Billing Dashboard</h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400">View billing history and manage payments</p>
                        </div>
                      </div>
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/pricing')}
                      className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                          <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="text-left">
                          <h4 className="font-medium text-slate-900 dark:text-slate-100">Upgrade Plan</h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400">View and change your subscription</p>
                        </div>
                      </div>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UserProfileForm;
